import { useState, useEffect, useCallback, forwardRef, useImperativeHandle, memo, useRef } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import {
    Plus, Trash2, Save, Search, Minus, Wrench, Weight, Package, ChevronDown, ChevronUp, RefreshCw, Sparkles
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = [
    { value: 'encombrant', label: 'Encombrant' },
    { value: 'dechetterie', label: 'Dechetterie' },
    { value: 'dem_2', label: 'Dem 2' },
    { value: 'cave_garage', label: 'Cave/Garage' },
    { value: 'charge_2', label: 'Charge 2' },
    { value: 'reste_non_inclus', label: 'Reste/Non inclus' },
];

const ALL_CATEGORIES = [
    { value: 'dem_1', label: 'Dem 1' },
    ...CATEGORIES,
];

const SUGGESTIONS = [
    // Salon
    'Canape 3 places', 'Canape 2 places', 'Canape angle', 'Canape convertible', 'Fauteuil', 'Fauteuil relax',
    'Table basse', 'Table basse relevable', 'Meuble TV', 'Bibliotheque', 'Vitrine', 'Buffet', 'Bahut',
    'Tapis', 'Lampadaire', 'Lampe de salon', 'Pouf', 'Banquette',
    // Salle a manger
    'Table a manger', 'Table ronde', 'Table extensible', 'Chaises', 'Chaise haute bebe', 'Banc',
    'Vaisselier', 'Console', 'Desserte',
    // Chambre
    'Lit double', 'Lit simple', 'Lit superpose', 'Lit mezzanine', 'Lit bebe', 'Lit coffre',
    'Matelas 1 place', 'Matelas 2 places', 'Sommier', 'Table de chevet', 'Coiffeuse',
    'Armoire 2 portes', 'Armoire 3 portes', 'Armoire 4 portes', 'Dressing', 'Penderie',
    'Commode', 'Commode 3 tiroirs', 'Commode 5 tiroirs', 'Semainier', 'Coffre a jouets',
    // Bureau
    'Bureau', 'Bureau angle', 'Chaise de bureau', 'Fauteuil de bureau', 'Caisson bureau',
    'Etagere', 'Etagere murale', 'Bibliotheque bureau', 'Classeur', 'Armoire de bureau',
    'Imprimante', 'Ecran PC', 'Tour PC', 'Ordinateur portable',
    // Cuisine / Electromenager
    'Refrigerateur', 'Frigo americain', 'Congelateur', 'Congelateur coffre',
    'Lave-vaisselle', 'Micro-ondes', 'Four', 'Four encastrable', 'Plaque de cuisson',
    'Machine a laver', 'Seche-linge', 'Lave-linge sechant', 'Hotte aspirante',
    'Robot cuisine', 'Robot patissier', 'Machine a cafe', 'Bouilloire',
    'Meuble cuisine', 'Desserte cuisine', 'Table haute', 'Tabouret de bar',
    // Salle de bain
    'Meuble vasque', 'Meuble salle de bain', 'Colonne salle de bain', 'Miroir salle de bain',
    'Panier a linge', 'Seche-serviettes',
    // Instruments de musique
    'Piano droit', 'Piano a queue', 'Piano numerique', 'Clavier', 'Guitare', 'Batterie',
    'Violoncelle', 'Contrebasse', 'Harpe', 'Synthetiseur',
    // Sport / Loisirs
    'Velo', 'Velo elliptique', 'Tapis de course', 'Rameur', 'Banc de musculation',
    'Table de ping-pong', 'Babyfoot', 'Billard', 'Trampoline',
    // Exterieur / Jardin
    'Table de jardin', 'Chaises de jardin', 'Parasol', 'Barbecue', 'Plancha',
    'Tondeuse', 'Brouette', 'Outils jardin', 'Salon de jardin', 'Balancelle',
    // Cave / Garage
    'Etabli', 'Armoire metallique', 'Etagere garage', 'Coffre de rangement',
    'Pneus', 'Outillage', 'Escabeau', 'Echelle',
    // Divers
    'Miroir', 'Grand miroir', 'Tableau', 'Cadre photo', 'Horloge murale',
    'Aspirateur', 'Aspirateur robot', 'Ventilateur', 'Climatiseur mobile', 'Radiateur',
    'Valise', 'Sac de voyage', 'Plantes', 'Pot de fleurs', 'Aquarium',
    'Cage animaux', 'Niche', 'Poussette', 'Siege auto',
    'Televiseur', 'Home cinema', 'Barre de son', 'Enceinte', 'Console de jeux',
    // Cartons
    'Carton standard', 'Carton livre', 'Carton fragile', 'Carton barrel', 'Penderie',
    'Housse matelas', 'Housse canape',
    // Divers demenagement
    'Divers', 'Sacs poubelle', 'Papier bulle', 'Couverture demenagement',
];

const DEFAULT_VOLUMES = {
    'Carton standard': 0.1, 'Carton livre': 0.05, 'Carton fragile': 0.1, 'Carton barrel': 0.3, 'Penderie': 0.4,
};

function getCatLabel(catValue) {
    return ALL_CATEGORIES.find(c => c.value === catValue)?.label || catValue;
}

/* ===================== INLINE CALCULATOR ===================== */
function InlineCalculator({ items, onUpdateItems, onSave, saving }) {
    const [searchTerm, setSearchTerm] = useState('');
    const [newVolume, setNewVolume] = useState('');
    const [newQty, setNewQty] = useState(1);
    const [newCategory, setNewCategory] = useState('dem_1');
    const [newDemontage, setNewDemontage] = useState(false);
    const [newObjetLourd, setNewObjetLourd] = useState(false);
    const [showSuggestions, setShowSuggestions] = useState(false);

    // AI parser state
    const [aiEnabled, setAiEnabled] = useState(false);
    const [aiText, setAiText] = useState('');
    const [aiLoading, setAiLoading] = useState(false);

    const handleAIParse = async () => {
        const text = aiText.trim();
        if (!text) { toast.error('Collez un texte à analyser'); return; }
        setAiLoading(true);
        try {
            const res = await axios.post(`${API_URL}/api/inventaire/parse-ai`, { text }, { withCredentials: true });
            const newItems = res.data.items || [];
            if (!newItems.length) { toast.error('Aucun article reconnu'); return; }
            // Override category & flags with the user's current selection at the bottom
            // (so cocher Encombrant + Demontage AVANT d'analyser applique ces réglages à tous les items extraits)
            const stamped = newItems.map(it => ({
                ...it,
                category: newCategory || it.category || 'dem_1',
                demontage: newDemontage || it.demontage || false,
                objet_lourd: newObjetLourd || it.objet_lourd || false,
            }));
            onUpdateItems([...stamped, ...items]);
            setAiText('');
            const tags = [];
            if (newCategory && newCategory !== 'dem_1') tags.push(getCatLabel(newCategory));
            if (newDemontage) tags.push('Démontage');
            if (newObjetLourd) tags.push('Objet lourd');
            const suffix = tags.length ? ` · ${tags.join(' · ')}` : '';
            toast.success(`${newItems.length} article(s) ajouté(s)${suffix}`);
        } catch (err) {
            toast.error(err.response?.data?.detail || "Erreur lors de l'analyse IA", { duration: 6000 });
        } finally {
            setAiLoading(false);
        }
    };

    const filteredSuggestions = searchTerm.trim()
        ? SUGGESTIONS.filter(s => s.toLowerCase().includes(searchTerm.toLowerCase()))
        : [];

    const handleSelectSuggestion = (name) => {
        setSearchTerm(name);
        const defaultVol = DEFAULT_VOLUMES[name];
        if (defaultVol !== undefined) setNewVolume(defaultVol.toString());
        setShowSuggestions(false);
    };

    const handleAddItem = () => {
        if (!searchTerm.trim()) { toast.error('Entrez le nom de l\'article'); return; }
        const vol = parseFloat(newVolume) || 0;
        if (vol <= 0) { toast.error('Le volume doit etre superieur a 0'); return; }
        const newItem = {
            name: searchTerm.trim(), volume: vol, quantity: parseInt(newQty) || 1,
            category: newCategory, demontage: newDemontage, objet_lourd: newObjetLourd, note: '',
        };
        onUpdateItems([newItem, ...items]);
        setSearchTerm(''); setNewVolume(''); setNewQty(1); setNewCategory('dem_1');
        setNewDemontage(false); setNewObjetLourd(false);
        toast.success(`${newItem.name} ajoute`);
    };

    const handleRemoveItem = (idx) => {
        onUpdateItems(items.filter((_, i) => i !== idx));
    };

    const handleUpdateItem = (idx, field, value) => {
        const updated = [...items];
        if (field === 'volume') {
            const v = parseFloat(value);
            updated[idx] = { ...updated[idx], volume: isNaN(v) || v < 0 ? 0 : v };
        } else if (field === 'quantity') {
            const q = parseInt(value, 10);
            updated[idx] = { ...updated[idx], quantity: isNaN(q) || q < 1 ? 1 : q };
        }
        onUpdateItems(updated);
    };

    return (
        <div className="bg-white rounded-2xl border border-[#E2E8F0] shadow-sm p-5 mb-5" data-testid="inline-calculator">
            <div className="flex items-center justify-between mb-4">
                <h3 className="text-base font-semibold text-[#0F172A]">Calculateur de volume</h3>
                <Button onClick={onSave} disabled={saving} size="sm"
                    className="bg-[#1B365D] hover:bg-[#112240] text-white gap-1.5 h-8 text-xs rounded-lg" data-testid="save-calc-btn">
                    {saving ? <RefreshCw className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
                    Enregistrer
                </Button>
            </div>

            {/* AI Parser */}
            <div className="mb-3" data-testid="ai-parser-block">
                <label className="flex items-center gap-2 cursor-pointer select-none w-fit">
                    <input
                        type="checkbox"
                        checked={aiEnabled}
                        onChange={(e) => setAiEnabled(e.target.checked)}
                        className="w-4 h-4 accent-[#1B365D] cursor-pointer"
                        data-testid="ai-parser-toggle"
                    />
                    <span className="inline-flex items-center gap-1.5 text-xs font-medium text-[#1B365D]">
                        <Sparkles className="w-3.5 h-3.5 text-[#C9A962]" />
                        Analyser un texte avec l'IA
                    </span>
                </label>
                {aiEnabled && (
                    <div className="mt-2 rounded-xl border border-[#1B365D]/15 bg-gradient-to-b from-[#1B365D]/[0.03] to-transparent p-3" data-testid="ai-parser-panel">
                        <textarea
                            value={aiText}
                            onChange={(e) => setAiText(e.target.value)}
                            placeholder="Collez ici le listing du client. Ex : « 1 canapé 3 places, 2 fauteuils, 1 lit double, 1 frigo américain, 5 cartons... »"
                            rows={4}
                            disabled={aiLoading}
                            className="w-full bg-white border border-[#E2E8F0] rounded-lg px-3 py-2 text-sm text-[#0F172A] focus:outline-none focus:ring-2 focus:ring-[#1B365D]/20 focus:border-[#1B365D] resize-y"
                            data-testid="ai-parser-textarea"
                        />
                        <div className="flex items-center justify-between mt-2 gap-2 flex-wrap">
                            <span className="text-[11px] text-[#64748B]">
                                L'IA détectera les articles, quantités et estimera le volume.
                            </span>
                            <Button
                                size="sm"
                                onClick={handleAIParse}
                                disabled={aiLoading || !aiText.trim()}
                                className="bg-[#C9A962] hover:bg-[#B8985A] text-white gap-1.5 h-8 text-xs rounded-lg"
                                data-testid="ai-parser-submit"
                            >
                                {aiLoading ? <RefreshCw className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
                                {aiLoading ? 'Analyse en cours...' : 'Analyser avec IA'}
                            </Button>
                        </div>
                    </div>
                )}
            </div>

            {/* Search */}
            <div className="relative mb-3">
                <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#94A3B8]" />
                    <input value={searchTerm}
                        onChange={(e) => { setSearchTerm(e.target.value); setShowSuggestions(true); }}
                        onFocus={() => setShowSuggestions(true)}
                        onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
                        placeholder="Rechercher un article..."
                        className="w-full bg-white border border-[#E2E8F0] rounded-xl pl-9 pr-4 py-2.5 text-[#0F172A] text-sm focus:outline-none focus:ring-2 focus:ring-[#1B365D]/20 focus:border-[#1B365D] transition-all"
                        data-testid="calc-search" />
                </div>
                {showSuggestions && filteredSuggestions.length > 0 && (
                    <div className="absolute z-20 w-full mt-1 bg-white border border-[#E2E8F0] rounded-xl shadow-lg max-h-40 overflow-y-auto">
                        {filteredSuggestions.slice(0, 8).map((s, i) => (
                            <button key={i} className="w-full text-left px-3 py-2 text-sm hover:bg-[#F8FAFC] text-[#0F172A] transition-colors first:rounded-t-xl last:rounded-b-xl flex items-center justify-between"
                                onMouseDown={() => handleSelectSuggestion(s)}>
                                <span>{s}</span>
                                {DEFAULT_VOLUMES[s] && <span className="text-xs text-[#94A3B8]">{DEFAULT_VOLUMES[s]} m³</span>}
                            </button>
                        ))}
                    </div>
                )}
            </div>

            {/* Volume + Qty + Add */}
            <div className="flex items-end gap-2 mb-3 flex-wrap">
                <div className="flex-1 min-w-[80px]">
                    <label className="block text-xs font-medium text-[#0F172A] mb-1">Volume (m³)</label>
                    <input type="number" step="0.01" min="0" value={newVolume}
                        onChange={(e) => setNewVolume(e.target.value)} placeholder="0.00"
                        className="w-full bg-white border border-[#E2E8F0] rounded-lg px-3 py-2 text-[#0F172A] text-sm focus:outline-none focus:ring-2 focus:ring-[#1B365D]/20 focus:border-[#1B365D]"
                        data-testid="calc-volume" />
                </div>
                <div className="w-[140px] shrink-0">
                    <label className="block text-xs font-medium text-[#0F172A] mb-1">Qte</label>
                    <div className="flex items-center border border-[#E2E8F0] rounded-lg bg-white overflow-hidden h-[44px]">
                        <button
                            type="button"
                            className="px-3 h-full text-[#475569] hover:bg-[#F1F5F9] active:bg-[#E2E8F0] transition-colors"
                            onClick={() => setNewQty(Math.max(1, newQty - 1))}
                            aria-label="Diminuer la quantité"
                            data-testid="calc-qty-minus"
                        >
                            <Minus className="w-4 h-4" />
                        </button>
                        <input
                            type="number"
                            min="1"
                            step="1"
                            value={newQty}
                            onChange={(e) => {
                                const raw = e.target.value;
                                if (raw === '') { setNewQty(1); return; }
                                const n = parseInt(raw, 10);
                                if (!isNaN(n) && n >= 1) setNewQty(n);
                            }}
                            className="flex-1 w-0 text-center font-semibold text-[#0F172A] text-sm bg-transparent border-0 outline-none focus:ring-0 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                            data-testid="calc-qty-input"
                        />
                        <button
                            type="button"
                            className="px-3 h-full text-[#475569] hover:bg-[#F1F5F9] active:bg-[#E2E8F0] transition-colors"
                            onClick={() => setNewQty(newQty + 1)}
                            aria-label="Augmenter la quantité"
                            data-testid="calc-qty-plus"
                        >
                            <Plus className="w-4 h-4" />
                        </button>
                    </div>
                </div>
                <button onClick={handleAddItem}
                    className="shrink-0 bg-[#1B365D] hover:bg-[#112240] text-white font-medium rounded-lg px-4 h-[44px] transition-colors flex items-center gap-1.5 text-sm"
                    data-testid="calc-add-btn">
                    <Plus className="w-3.5 h-3.5" /> Ajouter
                </button>
            </div>

            {/* Categories */}
            <div className="flex flex-wrap gap-1.5 mb-3">
                {CATEGORIES.map((cat) => (
                    <button key={cat.value} onClick={() => setNewCategory(prev => prev === cat.value ? 'dem_1' : cat.value)}
                        className={`px-2.5 py-1.5 rounded-lg border text-xs font-medium transition-all ${
                            newCategory === cat.value
                                ? 'border-[#1B365D] bg-[#1B365D]/5 text-[#1B365D] ring-1 ring-[#1B365D]'
                                : 'border-[#E2E8F0] bg-white text-[#475569] hover:border-[#1B365D]/30'
                        }`}>
                        {cat.label}
                    </button>
                ))}
                <span className="text-[10px] text-[#94A3B8] self-center ml-1">(Dem 1 par defaut)</span>
            </div>

            {/* Checkboxes */}
            <div className="flex gap-2 mb-4">
                <button onClick={() => setNewDemontage(!newDemontage)}
                    className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-xs transition-all ${
                        newDemontage ? 'border-[#1B365D] bg-[#1B365D]/5 text-[#1B365D]' : 'border-[#E2E8F0] text-[#475569]'
                    }`}>
                    <Wrench className={`w-3.5 h-3.5 ${newDemontage ? 'text-[#C9A962]' : 'text-[#94A3B8]'}`} />
                    Demontage
                </button>
                <button onClick={() => setNewObjetLourd(!newObjetLourd)}
                    className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-xs transition-all ${
                        newObjetLourd ? 'border-[#1B365D] bg-[#1B365D]/5 text-[#1B365D]' : 'border-[#E2E8F0] text-[#475569]'
                    }`}>
                    <Weight className={`w-3.5 h-3.5 ${newObjetLourd ? 'text-[#C9A962]' : 'text-[#94A3B8]'}`} />
                    Objet lourd
                </button>
            </div>

            {/* Compact items list with edit + delete */}
            {items.length > 0 && (
                <div className="border border-[#E2E8F0] rounded-lg overflow-hidden" data-testid="calc-items-list">
                    <div className="grid grid-cols-[1fr_70px_55px_55px_36px] bg-[#F8FAFC] px-3 py-2 text-[10px] font-semibold text-[#475569] uppercase tracking-wider border-b border-[#E2E8F0]">
                        <span>Article</span><span className="text-center">Vol.</span><span className="text-center">Qte</span><span className="text-center">Total</span><span></span>
                    </div>
                    <div className="max-h-[250px] overflow-y-auto">
                        {items.map((item, idx) => {
                            const total = (parseFloat(item.volume) || 0) * (parseInt(item.quantity) || 0);
                            const rowKey = `${idx}-${item.name}-${(parseFloat(item.volume) || 0).toFixed(2)}-${item.quantity}-${items.length}`;
                            return (
                                <div key={rowKey} className="grid grid-cols-[1fr_70px_55px_55px_36px] items-center gap-1 px-3 py-1.5 border-b border-[#F1F5F9] last:border-0 hover:bg-[#F8FAFC]/50 text-xs">
                                    <div className="truncate min-w-0">
                                        <span className="text-[#0F172A]">{item.name}</span>
                                        <span className="text-[9px] text-[#94A3B8] ml-1">{getCatLabel(item.category)}</span>
                                    </div>
                                    <input
                                        type="number"
                                        step="0.01"
                                        min="0"
                                        defaultValue={(parseFloat(item.volume) || 0).toFixed(2)}
                                        onBlur={(e) => handleUpdateItem(idx, 'volume', e.target.value)}
                                        onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
                                        className="w-full text-center bg-white border border-[#E2E8F0] rounded px-1 py-0.5 text-[#475569] text-xs focus:outline-none focus:ring-1 focus:ring-[#1B365D]/30 focus:border-[#1B365D]"
                                        data-testid={`calc-edit-volume-${idx}`}
                                    />
                                    <input
                                        type="number"
                                        step="1"
                                        min="1"
                                        defaultValue={item.quantity}
                                        onBlur={(e) => handleUpdateItem(idx, 'quantity', e.target.value)}
                                        onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
                                        className="w-full text-center bg-white border border-[#E2E8F0] rounded px-1 py-0.5 font-medium text-[#0F172A] text-xs focus:outline-none focus:ring-1 focus:ring-[#1B365D]/30 focus:border-[#1B365D]"
                                        data-testid={`calc-edit-qty-${idx}`}
                                    />
                                    <span className="text-center font-semibold text-[#1B365D] tabular-nums">{total.toFixed(2)}</span>
                                    <button
                                        type="button"
                                        className="text-[#EF4444] hover:bg-red-50 active:bg-red-100 rounded p-1.5 transition-colors flex items-center justify-center"
                                        onClick={() => handleRemoveItem(idx)}
                                        data-testid={`calc-remove-${idx}`}
                                        title="Supprimer cet article"
                                    >
                                        <Trash2 className="w-3.5 h-3.5" />
                                    </button>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}
        </div>
    );
}

/* ===================== VOLUME SUMMARY BLOCKS ===================== */
const CARTON_NAMES = ['carton standard', 'carton livre', 'carton barrel', 'penderie', 'carton fragile'];

function isCartonItem(item) {
    return CARTON_NAMES.some(cn => item.name.toLowerCase().includes(cn));
}

function CategoryCard({ title, catItems, accent, merge }) {
    // Merge items with same name (sum quantities & volumes)
    const displayItems = merge ? Object.values(catItems.reduce((acc, item) => {
        const key = item.name.toLowerCase();
        if (!acc[key]) {
            acc[key] = { ...item, quantity: parseInt(item.quantity) || 0 };
        } else {
            acc[key].quantity += parseInt(item.quantity) || 0;
        }
        return acc;
    }, {})) : catItems;

    const catVol = displayItems.reduce((s, item) => s + ((parseFloat(item.volume) || 0) * (parseInt(item.quantity) || 0)), 0);
    const catCount = displayItems.reduce((s, item) => s + (parseInt(item.quantity) || 0), 0);
    return (
        <div className="bg-white rounded-xl border border-[#E2E8F0] overflow-hidden flex flex-col">
            <div className="flex items-center justify-between px-4 py-3 bg-[#F8FAFC] border-b border-[#E2E8F0]">
                <div className="flex items-center gap-2">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold ${accent || 'bg-[#1B365D]/10 text-[#1B365D]'}`}>{title}</span>
                    <span className="text-xs text-[#94A3B8]">{catCount} article{catCount !== 1 ? 's' : ''}</span>
                </div>
                <span className="text-base font-bold text-[#1B365D]">{catVol.toFixed(2)} <span className="text-xs font-normal text-[#C9A962]">m³</span></span>
            </div>
            <div className="divide-y divide-[#F1F5F9] flex-1">
                {displayItems.map((item, idx) => {
                    const t = (parseFloat(item.volume) || 0) * (parseInt(item.quantity) || 0);
                    return (
                        <div key={idx} className="flex items-center px-4 py-2.5 text-sm">
                            <span className="text-[#0F172A] truncate flex-1">{item.name}</span>
                            <span className="text-[#1B365D] font-semibold text-xs w-10 text-center shrink-0">{item.quantity}</span>
                            <span className="text-[#475569] text-xs w-16 text-right shrink-0">{t.toFixed(2)} m³</span>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

function VolumeSummaryBlocks({ items }) {
    const [showDetails, setShowDetails] = useState(false);

    if (!items || items.length === 0) {
        return (
            <div className="text-center py-8 text-[#94A3B8]">
                <Package className="w-8 h-8 mx-auto mb-2 text-[#CBD5E1]" />
                <p className="text-sm">Aucun article dans l'inventaire</p>
            </div>
        );
    }

    const totalVolume = items.reduce((sum, item) => sum + ((parseFloat(item.volume) || 0) * (parseInt(item.quantity) || 0)), 0);
    const totalObjects = items.reduce((sum, item) => sum + (parseInt(item.quantity) || 0), 0);

    // ALL items go into their category (no exclusion)
    const catGroups = {};
    items.forEach(item => {
        const cat = item.category || 'dem_1';
        if (!catGroups[cat]) catGroups[cat] = [];
        catGroups[cat].push(item);
    });
    const nonEmptyCats = Object.entries(catGroups).filter(([, ci]) => ci.length > 0);

    // Special cross-cutting blocks (items appear here AND in their category)
    const cartonItems = items.filter(isCartonItem);
    const lourdItems = items.filter(item => item.objet_lourd);
    const demontageItems = items.filter(item => item.demontage);

    return (
        <div className="space-y-4" data-testid="volume-summary">
            {/* Total Block (navy) - compact, no item list */}
            <div className="bg-[#1B365D] text-white rounded-xl p-5" data-testid="volume-block-total">
                <div className="flex items-center justify-between">
                    <div>
                        <p className="text-xs font-semibold uppercase tracking-wider text-[#94A3B8]">Volume total</p>
                        <p className="text-sm text-white/70 mt-0.5">{totalObjects} article{totalObjects !== 1 ? 's' : ''}</p>
                    </div>
                    <p className="text-3xl font-bold flex items-baseline gap-1">
                        {totalVolume.toFixed(2)}
                        <span className="text-lg font-normal text-[#C9A962]">m³</span>
                    </p>
                </div>
            </div>

            {/* Toggle details */}
            <button
                onClick={() => setShowDetails(!showDetails)}
                className="w-full text-center py-2 text-sm font-medium text-[#1B365D] hover:text-[#C9A962] transition-colors flex items-center justify-center gap-1.5"
                data-testid="toggle-volume-details"
            >
                {showDetails ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                {showDetails ? 'Masquer les details' : 'Voir les details'}
            </button>

            {/* Category cards grid - hidden by default */}
            {showDetails && (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                {nonEmptyCats.map(([catValue, catItems]) => (
                    <CategoryCard key={catValue} title={getCatLabel(catValue)} catItems={catItems} />
                ))}
                {lourdItems.length > 0 && (
                    <CategoryCard title="Objet lourd" catItems={lourdItems} accent="bg-red-100 text-red-700" />
                )}
                {demontageItems.length > 0 && (
                    <CategoryCard title="Demontage / Remontage" catItems={demontageItems} accent="bg-indigo-100 text-indigo-700" />
                )}
                {cartonItems.length > 0 && (
                    <CategoryCard title="Cartons" catItems={cartonItems} accent="bg-amber-100 text-amber-800" merge />
                )}
            </div>
            )}
        </div>
    );
}

/* ===================== MAIN VOLUME SECTION ===================== */
const VolumeSection = forwardRef(function VolumeSection({ prospectId, onExternalSave }, ref) {
    const [items, setItems] = useState([]);
    const [eventId, setEventId] = useState(null);
    const [eventType, setEventType] = useState('');
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [showCalculator, setShowCalculator] = useState(false);
    // SAFETY NET — only allow saves AFTER the initial fetch returned
    // successfully. Without this, a parent triggering save() too early
    // (before fetchData resolved, or when fetchData failed silently with
    // 401/network error) would PUT `items: []` and WIPE OUT the entire
    // inventory in MongoDB. Real production incident, see Mme Riyahi.
    const initialLoadDoneRef = useRef(false);
    const initialItemsCountRef = useRef(0);

    const isVisite = ['visite_presentiel', 'visite_virtuelle'].includes(eventType);
    const showCalcAlways = !isVisite;

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const res = await axios.get(`${API_URL}/api/prospects/${prospectId}/event-inventaire`, { withCredentials: true });
            const fetched = res.data.items || [];
            setItems(fetched);
            setEventId(res.data.event_id);
            setEventType(res.data.event_type || '');
            // Only mark as "successfully loaded" once we have a real
            // response. Track how many items were loaded so we can refuse
            // a destructive save (empty array) that would wipe persisted
            // data the user never intended to touch.
            initialLoadDoneRef.current = true;
            initialItemsCountRef.current = fetched.length;
        } catch {
            // Critical: leave initialLoadDoneRef = false on failure so
            // saveInventory() refuses to PUT and silently wipe data.
        }
        finally { setLoading(false); }
    }, [prospectId]);

    useEffect(() => { fetchData(); }, [fetchData]);

    // Internal save of inventory only (silent = no toast, used when chained)
    const saveInventory = useCallback(async (silent = false) => {
        // SAFETY NET #1 — never PUT before the initial GET resolved.
        // This blocks the race that wiped Mme Riyahi : parent calls
        // save() too early, items state is still [] (initial), backend
        // would overwrite the existing inventory in MongoDB. The backend
        // has a complementary 409 guard (refuses empty PUT on non-empty
        // record), but this front guard avoids the noisy error round-trip.
        if (!initialLoadDoneRef.current) {
            // eslint-disable-next-line no-console
            console.warn('[VolumeSection] saveInventory blocked: initial fetch not done');
            return false;
        }
        try {
            // If the user *intentionally* emptied an inventory that had
            // items, we must explicitly opt in to force_empty so the
            // backend safety net lets the wipe through.
            const payload = { event_id: eventId, items };
            if (items.length === 0 && initialItemsCountRef.current > 0) {
                if (!silent) {
                    const ok = window.confirm(
                        `Vous etes sur le point de supprimer TOUS les ${initialItemsCountRef.current} articles de l\'inventaire. Continuer ?`,
                    );
                    if (!ok) return false;
                } else {
                    // Silent chained save with empty items + non-empty baseline
                    // looks like the race condition. Refuse to be safe — the
                    // user can still wipe via the explicit calculator button.
                    // eslint-disable-next-line no-console
                    console.warn(`[VolumeSection] saveInventory blocked (silent wipe of ${initialItemsCountRef.current} items)`);
                    return false;
                }
                payload.force_empty = true;
            }
            await axios.put(`${API_URL}/api/prospects/${prospectId}/event-inventaire`,
                payload, { withCredentials: true });
            if (!silent) toast.success('Inventaire sauvegarde');
            initialItemsCountRef.current = items.length;
            return true;
        } catch (err) {
            const detail = err?.response?.data?.detail;
            if (!silent) toast.error(typeof detail === 'string' ? detail : 'Erreur sauvegarde');
            return false;
        }
    }, [prospectId, eventId, items]);

    // Exposed to parent via ref
    useImperativeHandle(ref, () => ({
        save: () => saveInventory(true),
    }), [saveInventory]);

    // "Enregistrer" button on calculator saves EVERYTHING (prospect + inventory)
    const handleSave = async () => {
        setSaving(true);
        try {
            // 1) Save the prospect (if callback provided by parent)
            if (onExternalSave) {
                await onExternalSave({ silent: true });
            }
            // 2) Save the inventory
            await saveInventory(true);
            toast.success('Prospect et inventaire sauvegardes');
        } catch { toast.error('Erreur sauvegarde'); }
        finally { setSaving(false); }
    };

    if (loading) {
        return (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 border-l-4 border-l-[#1B365D] p-5">
                <div className="flex items-center justify-center h-16">
                    <RefreshCw className="w-5 h-5 animate-spin text-gray-400" />
                </div>
            </div>
        );
    }

    return (
        <div data-testid="section-volume">
            {/* Calculator: always visible for listing/no-event, toggle for visite */}
            {isVisite && (
                <div className="mb-3 flex justify-end">
                    <Button variant="outline" size="sm" onClick={() => setShowCalculator(!showCalculator)}
                        className="gap-1.5 text-xs h-8 border-[#1B365D]/20 text-[#1B365D] hover:bg-[#1B365D]/5"
                        data-testid="toggle-calculator-btn">
                        {showCalculator ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                        {showCalculator ? 'Masquer calculateur' : 'Ajouter / Supprimer articles'}
                    </Button>
                </div>
            )}

            {(showCalcAlways || showCalculator) && (
                <InlineCalculator items={items} onUpdateItems={setItems} onSave={handleSave} saving={saving} />
            )}

            {/* Volume Summary Section */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 border-l-4 border-l-[#1B365D] overflow-hidden">
                <div className="px-5 py-4 border-b border-gray-100 flex items-center gap-2">
                    <Package className="w-4 h-4 text-[#1B365D]" />
                    <h3 className="text-base font-bold text-gray-900">Volume</h3>
                </div>
                <div className="p-5">
                    <VolumeSummaryBlocks items={items} />
                </div>
            </div>
        </div>
    );
});

export default memo(VolumeSection);
